import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-provider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetBody, SheetFooter } from "@/components/ui/sheet";
import {
  completeMagicLink,
  isMagicLinkReturn,
  sendMagicLink,
  sendPasswordReset,
  signInWithEmail,
  signInWithProvider,
  signUpWithEmail,
  stashedMagicLinkEmail,
  type OAuthProviderName,
} from "@/lib/api";

// ─── Schemas ─────────────────────────────────────────────────
const signInSchema = z.object({
  email: z.string().email("That doesn't look like an email"),
  password: z.string().min(6, "At least 6 characters"),
});
const signUpSchema = signInSchema.extend({
  name: z.string().min(1, "We'll use this to greet you"),
});
type SignInValues = z.infer<typeof signInSchema>;
type SignUpValues = z.infer<typeof signUpSchema>;

export default function AuthRoute() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const from = (location.state as { from?: string } | null)?.from ?? "/dashboard";

  // Magic-link return handoff: when the /auth URL includes the Firebase
  // sign-in-link tokens, complete the flow instead of showing the form.
  // Firebase pushes the user back here (the `url` we passed on send).
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isMagicLinkReturn(window.location.href)) return;
    const email =
      stashedMagicLinkEmail() ?? window.prompt("Confirm your email to sign in") ?? "";
    if (!email) return;
    (async () => {
      try {
        await completeMagicLink(email, window.location.href);
        toast.success("You're in.");
        navigate("/dashboard", { replace: true });
      } catch (err) {
        toast.error(friendlyAuthError(err));
      }
    })();
  }, [navigate]);

  // Already signed in? Skip the screen.
  if (!loading && user) return <Navigate to={from} replace />;

  return (
    <main className="min-h-full bg-bg">
      <div className="mx-auto flex min-h-full max-w-md flex-col px-6 pt-16 pb-10">
        <Brand />
        <AuthCard />
        <Footer />
      </div>
    </main>
  );
}

// ─── Brand (logo + wordmark, entrance-animated) ──────────────
// The logo scales in from ~0.7 while the three color circles do a small
// stagger; the wordmark and tagline fade+rise after. Everything respects
// prefers-reduced-motion (framer-motion honors it by default).
function Brand() {
  return (
    <div className="mb-10 flex flex-col items-center gap-3 text-center">
      <motion.div
        initial={{ scale: 0.72, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          type: "spring",
          stiffness: 240,
          damping: 20,
          mass: 0.9,
        }}
        className="relative"
      >
        <BrandMark className="h-24 w-24 drop-shadow-[0_10px_24px_rgba(228,87,46,0.25)]" />
      </motion.div>

      <motion.h1
        initial={{ y: 8, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.18, duration: 0.35, ease: "easeOut" }}
        className="font-display leading-none tracking-tight text-ink m-0"
        style={{
          fontVariationSettings: `"opsz" 96, "wght" 500`,
          fontSize: "clamp(44px, 12vw, 64px)",
          letterSpacing: "-0.03em",
        }}
      >
        Trav<em className="italic text-primary" style={{ fontVariationSettings: `"opsz" 96, "wght" 400` }}>e</em>lix
      </motion.h1>

      <motion.p
        initial={{ y: 6, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.32, duration: 0.35, ease: "easeOut" }}
        className="max-w-[28ch] text-[15px] text-ink-muted m-0"
      >
        Where your friend group plans the trip and settles the tab.
      </motion.p>
    </div>
  );
}

// Inline logo — same three overlapping circles + compass arrow as the
// launcher icon, scaled to a 96-pt slot. Kept as SVG (not an <img>) so
// the fill colors respond to CSS custom properties later if we theme it.
function BrandMark({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 512 512"
      role="img"
      aria-label="Travelix"
      className={className}
    >
      <defs>
        <linearGradient id="brand-warm" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#FBF8F4" />
          <stop offset="1" stopColor="#F3EEE7" />
        </linearGradient>
      </defs>
      <rect width="512" height="512" rx="96" fill="url(#brand-warm)" />
      <g transform="translate(56 56) scale(0.78)">
        <g style={{ mixBlendMode: "multiply" }} shapeRendering="geometricPrecision">
          <motion.circle
            cx={336}
            cy={164}
            r={152}
            fill="#17A5AE"
            initial={{ scale: 0.4, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.08, type: "spring", stiffness: 220, damping: 18 }}
            style={{ transformOrigin: "336px 164px" }}
          />
          <motion.circle
            cx={156}
            cy={248}
            r={148}
            fill="#E4572E"
            initial={{ scale: 0.4, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.16, type: "spring", stiffness: 220, damping: 18 }}
            style={{ transformOrigin: "156px 248px" }}
          />
          <motion.circle
            cx={332}
            cy={360}
            r={140}
            fill="#F0902B"
            initial={{ scale: 0.4, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.24, type: "spring", stiffness: 220, damping: 18 }}
            style={{ transformOrigin: "332px 360px" }}
          />
        </g>
        <motion.g
          transform="translate(256 256) rotate(45) translate(-256 -256)"
          fill="#FFFFFF"
          initial={{ opacity: 0, rotate: -15, scale: 0.9 }}
          animate={{ opacity: 1, rotate: 0, scale: 1 }}
          transition={{ delay: 0.35, duration: 0.35, ease: "easeOut" }}
          style={{ transformOrigin: "256px 256px" }}
        >
          <path d="M 256 92 c -14 0 -25 11 -25 25 v 92 L 108 273 c -7 4 -11 11 -11 19 v 18 c 0 8 8 14 15 12 l 119 -33 v 62 l -28 21 c -3 2 -5 6 -5 10 v 14 c 0 4 4 8 8 6 l 45 -13 c 3 -1 7 -1 10 0 l 45 13 c 4 2 8 -2 8 -6 v -14 c 0 -4 -2 -8 -5 -10 l -28 -21 v -62 l 119 33 c 7 2 15 -4 15 -12 v -18 c 0 -8 -4 -15 -11 -19 L 281 209 v -92 c 0 -14 -11 -25 -25 -25 z" />
        </motion.g>
      </g>
    </svg>
  );
}

// ─── The card: tabbed sign in / sign up ──────────────────────
function AuthCard() {
  const [mode, setMode] = useState<"signin" | "signup">("signin");

  return (
    <section className="rounded-xl bg-bg-elevated border border-line shadow-warm-2 p-6">
      <div role="tablist" className="mb-6 flex gap-1 rounded-full bg-bg-sunken p-1">
        {(["signin", "signup"] as const).map((m) => (
          <button
            key={m}
            role="tab"
            aria-selected={mode === m}
            onClick={() => setMode(m)}
            className={
              "flex-1 rounded-full px-4 py-2 text-[13px] font-semibold transition-colors " +
              (mode === m
                ? "bg-bg-elevated text-ink shadow-warm-1"
                : "text-ink-subtle hover:text-ink")
            }
          >
            {m === "signin" ? "Sign in" : "Create account"}
          </button>
        ))}
      </div>

      <OAuthButtons />
      <OrDivider />

      {mode === "signin" ? <SignInForm /> : <SignUpForm />}
    </section>
  );
}

// ─── OAuth ───────────────────────────────────────────────────
// Google is live. Apple's code path is implemented but the button is
// gated behind VITE_APPLE_SSO_ENABLED (default false) — the Apple
// Developer program + Firebase console config still need to land per
// docs/decisions.md D-014. Flip the flag once those are ready; the
// UI switches from disabled to functional with no code change.
function OAuthButtons() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState<null | "google" | "apple">(null);
  const appleEnabled = import.meta.env.VITE_APPLE_SSO_ENABLED === "true";

  const run = async (provider: OAuthProviderName) => {
    setBusy(provider);
    try {
      await signInWithProvider(provider);
      toast.success(`Signed in with ${provider === "google" ? "Google" : "Apple"}.`);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(friendlyAuthError(err));
    } finally {
      setBusy(null);
    }
  };
  const runGoogle = () => run("google");
  const runApple = () => run("apple");

  return (
    <div className="mb-4 flex flex-col gap-2">
      <button
        type="button"
        onClick={runGoogle}
        disabled={busy !== null}
        className="inline-flex h-11 w-full items-center justify-center gap-2.5 rounded-full border border-line-strong bg-bg-elevated text-[14px] font-semibold text-ink transition-colors hover:bg-bg-sunken disabled:opacity-60"
      >
        <GoogleGlyph />
        {busy === "google" ? "Opening Google…" : "Continue with Google"}
      </button>

      {appleEnabled ? (
        <button
          type="button"
          onClick={runApple}
          disabled={busy !== null}
          className="inline-flex h-11 w-full items-center justify-center gap-2.5 rounded-full bg-[#111] text-[14px] font-semibold text-white transition-colors hover:bg-[#222] disabled:opacity-60"
        >
          <AppleGlyph />
          {busy === "apple" ? "Opening Apple…" : "Continue with Apple"}
        </button>
      ) : (
        <button
          type="button"
          disabled
          aria-disabled="true"
          aria-label="Continue with Apple — coming soon"
          className="inline-flex h-11 w-full items-center justify-center gap-2.5 rounded-full bg-[#111] text-[14px] font-semibold text-white/70 opacity-70 cursor-not-allowed"
        >
          <AppleGlyph />
          <span>Continue with Apple</span>
          <span className="ml-1 rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white/85">
            Soon
          </span>
        </button>
      )}
    </div>
  );
}

function OrDivider() {
  return (
    <div className="mb-4 flex items-center gap-3">
      <div className="h-px flex-1 bg-line" />
      <span className="text-[11px] font-bold uppercase tracking-widest text-ink-subtle">
        or with email
      </span>
      <div className="h-px flex-1 bg-line" />
    </div>
  );
}

/* Brand-colored Google mark — inlined SVG to keep the CSP simple. */
function GoogleGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
    </svg>
  );
}

function AppleGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden fill="currentColor">
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.36-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.36C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
    </svg>
  );
}

// ─── Sign in ─────────────────────────────────────────────────
function SignInForm() {
  const navigate = useNavigate();
  const [forgotOpen, setForgotOpen] = useState(false);
  const form = useForm<SignInValues>({
    resolver: zodResolver(signInSchema),
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = async (values: SignInValues) => {
    try {
      await signInWithEmail(values.email, values.password);
      toast.success("Welcome back.");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(friendlyAuthError(err));
    }
  };

  return (
    <>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <FieldGroup
          id="signin-email"
          label="Email"
          error={form.formState.errors.email?.message}
        >
          <Input
            id="signin-email"
            type="email"
            autoComplete="email"
            inputMode="email"
            placeholder="you@friends.com"
            invalid={!!form.formState.errors.email}
            {...form.register("email")}
          />
        </FieldGroup>
        <FieldGroup
          id="signin-password"
          label="Password"
          error={form.formState.errors.password?.message}
        >
          <Input
            id="signin-password"
            type="password"
            autoComplete="current-password"
            placeholder="At least 6 characters"
            invalid={!!form.formState.errors.password}
            {...form.register("password")}
          />
        </FieldGroup>

        <button
          type="button"
          onClick={() => setForgotOpen(true)}
          className="self-end text-[13px] font-medium text-primary hover:underline"
        >
          Forgot password?
        </button>

        <Button type="submit" block disabled={form.formState.isSubmitting}>
          {form.formState.isSubmitting ? "Signing in…" : "Sign in"}
        </Button>

        <MagicLinkButton getEmail={() => form.getValues("email").trim()} />
      </form>

      <ForgotPasswordSheet open={forgotOpen} onOpenChange={setForgotOpen} />
    </>
  );
}

/**
 * Passwordless sign-in link. On click, if the email field has a value,
 * ask Firebase to email a one-tap link; the user comes back to /auth
 * with the sign-in tokens in the URL and the outer AuthRoute completes
 * the flow (see the useEffect at the top of this file).
 *
 * Requires Email Link sign-in enabled in the Firebase console —
 * that's an out-of-band setup step; the code path here is idempotent.
 */
function MagicLinkButton({ getEmail }: { getEmail: () => string }) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const send = async () => {
    const email = getEmail();
    if (!email) {
      toast.error("Enter your email above first.");
      return;
    }
    setSending(true);
    try {
      await sendMagicLink(email);
      setSent(true);
      toast.success(`Check your inbox at ${email}.`);
    } catch (err) {
      toast.error(friendlyAuthError(err));
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <p className="mt-1 text-center text-[12px] text-ink-subtle m-0">
        We sent you a link. Open it on this device to sign in.
      </p>
    );
  }
  return (
    <button
      type="button"
      onClick={send}
      disabled={sending}
      className="text-[13px] font-medium text-primary hover:underline disabled:opacity-60"
    >
      {sending ? "Sending…" : "Email me a link instead"}
    </button>
  );
}

// ─── Sign up ─────────────────────────────────────────────────
function SignUpForm() {
  const navigate = useNavigate();
  const form = useForm<SignUpValues>({
    resolver: zodResolver(signUpSchema),
    defaultValues: { name: "", email: "", password: "" },
  });

  const onSubmit = async (values: SignUpValues) => {
    try {
      await signUpWithEmail(values.name, values.email, values.password);
      toast.success("You're in.");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(friendlyAuthError(err));
    }
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <FieldGroup
        id="signup-name"
        label="Your name"
        error={form.formState.errors.name?.message}
      >
        <Input
          id="signup-name"
          autoComplete="name"
          placeholder="What should the group call you?"
          invalid={!!form.formState.errors.name}
          {...form.register("name")}
        />
      </FieldGroup>
      <FieldGroup
        id="signup-email"
        label="Email"
        error={form.formState.errors.email?.message}
      >
        <Input
          id="signup-email"
          type="email"
          autoComplete="email"
          inputMode="email"
          placeholder="you@friends.com"
          invalid={!!form.formState.errors.email}
          {...form.register("email")}
        />
      </FieldGroup>
      <FieldGroup
        id="signup-password"
        label="Password"
        error={form.formState.errors.password?.message}
      >
        <Input
          id="signup-password"
          type="password"
          autoComplete="new-password"
          placeholder="At least 6 characters"
          invalid={!!form.formState.errors.password}
          {...form.register("password")}
        />
        <PasswordStrength value={form.watch("password") ?? ""} />
      </FieldGroup>

      <Button type="submit" block disabled={form.formState.isSubmitting}>
        {form.formState.isSubmitting ? "Creating your account…" : "Create account"}
      </Button>
    </form>
  );
}

// ─── Password strength meter ─────────────────────────────────
/**
 * Small four-pill meter under the sign-up password field. Scoring is
 * hand-rolled — length + character-class diversity — because zxcvbn
 * is a large dependency for a nudge that only appears once per user.
 *
 *   0 pills = empty
 *   1 pill (danger)   = < 8 chars
 *   2 pills (warning) = 8+ chars, 2 classes
 *   3 pills (warning) = 10+ chars, 3 classes
 *   4 pills (success) = 12+ chars, 4 classes
 *
 * The bar and label are aria-hidden; the label is announced through
 * an aria-live region below so screen readers hear the level only
 * when it changes.
 */
function PasswordStrength({ value }: { value: string }) {
  const score = scorePassword(value);
  if (!value) return null;
  const labels = ["Weak", "Okay", "Good", "Strong"];
  const tone = ["bg-danger", "bg-warning", "bg-warning", "bg-success"];
  return (
    <div className="mt-2">
      <div aria-hidden className="flex gap-1">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full ${
              i < score ? tone[score - 1] : "bg-bg-sunken"
            }`}
          />
        ))}
      </div>
      <p className="mt-1 text-[11px] text-ink-subtle m-0" aria-live="polite">
        {labels[score - 1] ?? ""}
      </p>
    </div>
  );
}

function scorePassword(pw: string): number {
  if (!pw) return 0;
  const classes =
    (/[a-z]/.test(pw) ? 1 : 0) +
    (/[A-Z]/.test(pw) ? 1 : 0) +
    (/\d/.test(pw) ? 1 : 0) +
    (/[^A-Za-z0-9]/.test(pw) ? 1 : 0);
  if (pw.length < 8) return 1;
  if (pw.length >= 12 && classes >= 4) return 4;
  if (pw.length >= 10 && classes >= 3) return 3;
  if (pw.length >= 8 && classes >= 2) return 2;
  return 1;
}

// ─── Forgot password sheet ───────────────────────────────────
function ForgotPasswordSheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const form = useForm<{ email: string }>({
    resolver: zodResolver(z.object({ email: z.string().email("Enter a valid email") })),
    defaultValues: { email: "" },
  });

  // Reset on close so it's blank on re-open
  useEffect(() => {
    if (!open) form.reset({ email: "" });
  }, [open, form]);

  const onSubmit = async ({ email }: { email: string }) => {
    try {
      await sendPasswordReset(email);
      toast.success("Check your inbox for the reset link.");
      onOpenChange(false);
    } catch (err) {
      toast.error(friendlyAuthError(err));
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader
          title="Reset your password"
          description="We'll email you a link. Should land in a minute or two."
        />
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <SheetBody>
            <FieldGroup
              id="forgot-email"
              label="Email"
              error={form.formState.errors.email?.message}
            >
              <Input
                id="forgot-email"
                type="email"
                autoComplete="email"
                inputMode="email"
                placeholder="you@friends.com"
                autoFocus
                invalid={!!form.formState.errors.email}
                {...form.register("email")}
              />
            </FieldGroup>
          </SheetBody>
          <SheetFooter>
            <Button type="submit" block disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? "Sending…" : "Send reset link"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              block
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}

// ─── Field group ─────────────────────────────────────────────
function FieldGroup({
  id,
  label,
  error,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {error ? (
        <p role="alert" className="text-[12px] text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

// ─── Legal footer ────────────────────────────────────────────
function Footer() {
  return (
    <p className="mt-8 text-center text-[12px] leading-relaxed text-ink-subtle">
      By continuing you agree to Travelix's{" "}
      <Link to="/legal/terms" className="text-ink-muted underline decoration-line-strong hover:text-primary">
        terms
      </Link>{" "}
      and{" "}
      <Link to="/legal/privacy" className="text-ink-muted underline decoration-line-strong hover:text-primary">
        privacy policy
      </Link>
      .
    </p>
  );
}

// ─── Error mapper ────────────────────────────────────────────
function friendlyAuthError(err: unknown): string {
  const code = (err as { code?: string })?.code ?? "";
  const map: Record<string, string> = {
    "auth/invalid-credential": "That email and password don't match.",
    "auth/wrong-password": "That email and password don't match.",
    "auth/user-not-found": "No account with that email yet.",
    "auth/email-already-in-use": "There's already an account with that email.",
    "auth/weak-password": "Try a longer password.",
    "auth/too-many-requests": "Too many attempts. Give it a minute.",
    "auth/network-request-failed": "Network hiccup — try again.",
    "auth/popup-closed-by-user": "Sign-in cancelled.",
    "auth/popup-blocked": "Your browser blocked the sign-in popup. Allow popups for Travelix.",
    "auth/account-exists-with-different-credential":
      "You already have an account with this email. Sign in with the original method.",
    "auth/operation-not-allowed":
      "This sign-in method isn't enabled yet. Contact support.",
    "auth/cancelled-popup-request": "Sign-in cancelled.",
  };
  return map[code] ?? "Something went sideways. Try again.";
}
